// Remembers the generation (or enhancement) a logged-out user asked for so it can
// continue after they create an account and are redirected back to /ai. The sign-in
// flow finishes with a full page reload (window.location.assign('/ai')), which wipes
// the in-memory store, so the intent and the form draft that produces it are stashed
// in localStorage instead. Best-effort; storage may be unavailable (private mode,
// quota). See GenerateButton (save on logged-out click) and useResumePendingGeneration
// (restore + replay on load).

import { setOpenedModel } from 'Pods/ThreeDStudio/Store/actions/ui'

import { threeDStudioStore } from '../Store'

export const PENDING_GENERATION_KEY = 'ai_studio_pending_generation'

// The intent is only meaningful right after the account-creation redirect. Bounding
// its lifetime stops a stale draft from replaying on an unrelated later login.
const MAX_AGE_MS = 30 * 60 * 1000

/**
 * Builds the serializable form snapshot needed to rebuild the generate/enhance UI
 * after a reload. Derived schema (generationModelSchema, availableProviders) is left
 * out because useActiveModelSchema recomputes it from the source fields below.
 * @param {Object} state - ThreeDStudio store state (getState())
 * @returns {Object} snapshot
 */
export const buildGenerationSnapshot = (state) => ({
  modelSettings: state.modelSettings,
  schema: {
    generationAction: state.schema.generationAction,
    generationProvider: state.schema.generationProvider,
    generationMode: state.schema.generationMode,
    enhancedAction: state.schema.enhancedAction,
    enhancedProvider: state.schema.enhancedProvider,
    enhancedMode: state.schema.enhancedMode,
  },
  remeshSettings: state.remeshSettings,
  textureSettings: state.textureSettings,
  ui: {
    activeViewerSidebarTab: state.ui.activeViewerSidebarTab,
    isViewerSidebarOpen: state.ui.isViewerSidebarOpen,
    isModelSettingsOpen: state.ui.isModelSettingsOpen,
    activeMobilePanel: state.ui.activeMobilePanel,
    openedModel: state.ui.openedModel,
  },
})

/**
 * Restores a snapshot into the store. An enhancement snapshot carries the opened
 * model, so setOpenedModel rebuilds the viewer/schema/model settings from it and
 * only the enhancement settings + tab are overlaid; a plain generation snapshot has
 * no opened model and restores the generate-form draft directly.
 * @param {Object} snapshot
 */
export const restoreGenerationSnapshot = (snapshot) => {
  if (!snapshot) {
    return
  }

  const { modelSettings, schema, remeshSettings, textureSettings, ui } = snapshot
  const openedModel = ui?.openedModel
  const hasOpenedModel = !!openedModel && Object.keys(openedModel).length > 0

  if (hasOpenedModel) {
    setOpenedModel(openedModel)
    threeDStudioStore.setState((state) => ({
      schema: {
        ...state.schema,
        ...(schema?.enhancedAction && { enhancedAction: schema.enhancedAction }),
        ...(schema?.enhancedProvider && { enhancedProvider: schema.enhancedProvider }),
        ...(schema?.enhancedMode && { enhancedMode: schema.enhancedMode }),
      },
      remeshSettings: { ...state.remeshSettings, ...(remeshSettings || {}) },
      textureSettings: { ...state.textureSettings, ...(textureSettings || {}) },
      ui: {
        ...state.ui,
        activeViewerSidebarTab: ui.activeViewerSidebarTab,
        isViewerSidebarOpen: ui.isViewerSidebarOpen,
        isModelSettingsOpen: ui.isModelSettingsOpen,
        activeMobilePanel: ui.activeMobilePanel,
      },
    }))

    return
  }

  threeDStudioStore.setState((state) => ({
    modelSettings: { ...state.modelSettings, ...(modelSettings || {}) },
    schema: {
      ...state.schema,
      ...(schema?.generationAction && { generationAction: schema.generationAction }),
      ...(schema?.generationProvider && { generationProvider: schema.generationProvider }),
      ...(schema?.generationMode && { generationMode: schema.generationMode }),
    },
    ui: {
      ...state.ui,
      ...(ui?.activeMobilePanel && { activeMobilePanel: ui.activeMobilePanel }),
    },
  }))
}

/**
 * Persists the pending generation intent + form snapshot.
 * @param {Object} payload
 * @param {string} payload.triggerKey - Trigger key of the button to replay
 * @param {boolean} payload.isEnhancementMode - Whether the click was an enhancement
 * @param {Object} payload.snapshot - Form snapshot from buildGenerationSnapshot
 */
export const savePendingGeneration = ({ triggerKey, isEnhancementMode, snapshot }) => {
  try {
    window.localStorage.setItem(
      PENDING_GENERATION_KEY,
      JSON.stringify({ triggerKey, isEnhancementMode, snapshot, savedAt: Date.now() }),
    )
  } catch (_) {
    // No-op.
  }
}

/**
 * Reads the pending generation intent, or null when absent, unparseable or expired.
 * @returns {Object | null}
 */
export const readPendingGeneration = () => {
  try {
    const raw = window.localStorage.getItem(PENDING_GENERATION_KEY)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw)
    if (!parsed?.triggerKey || typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      clearPendingGeneration()
      return null
    }

    return parsed
  } catch (_) {
    return null
  }
}

export const clearPendingGeneration = () => {
  try {
    window.localStorage.removeItem(PENDING_GENERATION_KEY)
  } catch (_) {
    // No-op.
  }
}
